"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  Search, Plus, Trash2, X, User, ShoppingCart,
  Loader2, AlertCircle, ArrowRight, Package,
} from "lucide-react"
import toast from "react-hot-toast"
import { API_ENDPOINTS } from "../../../constants/config"
import { fetchCustomers, createCustomer, fetchCustomerLedger, fetchLedgerSummary } from "../../../services/ledgerService"

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`

const MultiFilter = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val])
  }
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-[11.5px] px-2.5 py-1.5 rounded-lg border cursor-pointer flex items-center gap-1 ${
          selected.length > 0
            ? "bg-blue-50 border-blue-300 text-blue-700 font-semibold"
            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        {label}{selected.length > 0 && ` (${selected.length})`}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 top-[calc(100%+4px)] left-0 bg-white border border-gray-300 rounded-lg shadow-lg min-w-[160px] max-h-48 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-gray-400">No options</div>
          ) : (
            options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-[12px] text-gray-700">
                <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} className="rounded" />
                {opt.label}
              </label>
            ))
          )}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-[11px] text-red-500 hover:bg-red-50 py-1.5 border-t border-gray-100 cursor-pointer bg-transparent border-x-0 border-b-0">
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const BulkIssueDialog = ({ open, onOpenChange, preloadItem, onSuccess }) => {
  const [cartItems, setCartItems] = useState([])
  const [itemSearch, setItemSearch] = useState("")
  const [allItems, setAllItems] = useState([])
  const [searchedItems, setSearchedItems] = useState(null)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [customerSearch, setCustomerSearch] = useState("")
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [showCustomerDrop, setShowCustomerDrop] = useState(false)
  const [creatingCustomer, setCreatingCustomer] = useState(false)

  const [paymentMode, setPaymentMode] = useState("yes")
  const [issueTotal, setIssueTotal] = useState("")
  const [issuePaid, setIssuePaid] = useState("")
  const [issueDue, setIssueDue] = useState("")
  const [issueNotes, setIssueNotes] = useState("")
  const [issueDueDate, setIssueDueDate] = useState("")
  const [oldDues, setOldDues] = useState(0)
  const [loadingDues, setLoadingDues] = useState(false)

  const [statQtyToday, setStatQtyToday] = useState(0)
  const [statSkusToday, setStatSkusToday] = useState(0)
  const [editingQty, setEditingQty] = useState(null)
  const [filterCategory, setFilterCategory] = useState([])
  const [filterSubCategory, setFilterSubCategory] = useState([])
  const [filterBrand, setFilterBrand] = useState([])
  const [filterAvailability, setFilterAvailability] = useState([])
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState("asc")

  const searchInputRef = useRef(null)
  const customerDropRef = useRef(null)
  // Synchronous re-entrancy guard — `submitting` state alone can't stop a
  // double-click since React's state update is async.
  const submittingRef = useRef(false)

  useEffect(() => {
    if (open) {
      loadAllItems()
      loadIssueTodayStats()
      Promise.all([fetchCustomers({ sortBy: "recent_activity" }), fetchLedgerSummary()]).then(([custRes, ledgerRes]) => {
        const custs = custRes.data || []
        const ledgerMap = new Map((ledgerRes.data || []).map((s) => [s._id?.toString(), s.total_due || 0]))
        setCustomers(custs.map((c) => ({ ...c, total_due: ledgerMap.get(c._id?.toString()) || 0 })))
      }).catch(() => {})
      if (preloadItem) {
        setCartItems([{
          _id: preloadItem._id,
          inventory_id: preloadItem.inventory_id,
          item_name: preloadItem.item_name,
          sku: preloadItem.sku || `INV-${preloadItem.inventory_id}`,
          unit: preloadItem.unit_of_measurement || "pcs",
          available: preloadItem.quantity_available || 0,
          quantity: 1,
        }])
      }
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      resetForm()
    }
  }, [open])

  const resetForm = () => {
    setCartItems([])
    setItemSearch("")
    setCustomerSearch("")
    setSelectedCustomer(null)
    setShowCustomerDrop(false)
    setPaymentMode("yes")
    setIssueTotal("")
    setIssuePaid("")
    setIssueDue("")
    setIssueDueDate("")
    setIssueNotes("")
    setOldDues(0)
    setFilterCategory([])
    setFilterSubCategory([])
    setFilterBrand([])
    setFilterAvailability([])
    setSortCol(null)
    setSortDir("asc")
  }

  const loadAllItems = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(`${API_ENDPOINTS.inventory}?limit=500&activeStatus=active&paginate=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      setAllItems(data.data || [])
    } catch {}
  }

  const searchInventory = async (q) => {
    const token = localStorage.getItem("token")
    const res = await fetch(
      `${API_ENDPOINTS.inventory}?search=${encodeURIComponent(q)}&activeStatus=active&limit=200&paginate=true`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await res.json()
    return data.data || []
  }

  // Search the full catalog server-side — the initial `allItems` load is capped
  // (backend maxLimit) and won't contain every item, so filtering it client-side
  // misses matches outside that window.
  useEffect(() => {
    const q = itemSearch.trim()
    if (!q) {
      setSearchedItems(null)
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = setTimeout(() => {
      searchInventory(q)
        .then((items) => setSearchedItems(items))
        .catch(() => setSearchedItems([]))
        .finally(() => setSearching(false))
    }, 250)
    return () => clearTimeout(handle)
  }, [itemSearch])

  const loadIssueTodayStats = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventoryIssueTodayStats, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.status === "success") {
        setStatQtyToday(data.data.qty_issued_today || 0)
        setStatSkusToday(data.data.skus_issued_today || 0)
      }
    } catch {}
  }

  // --- Cart logic ---
  const addToCartByOne = (item) => {
    const existing = cartItems.find((c) => c.inventory_id === item.inventory_id)
    const avail = item.quantity_available ?? item.available ?? 0
    if (existing) {
      if (existing.quantity >= existing.available) {
        toast.error(`Only ${existing.available} of "${item.item_name}" available.`)
        return
      }
      setCartItems((prev) =>
        prev.map((c) => (c.inventory_id === item.inventory_id ? { ...c, quantity: c.quantity + 1 } : c))
      )
    } else {
      if (avail <= 0) { toast.error("Item is out of stock"); return }
      setCartItems((prev) => [
        ...prev,
        {
          _id: item._id,
          inventory_id: item.inventory_id,
          item_name: item.item_name,
          sku: item.sku || `INV-${item.inventory_id}`,
          unit: item.unit_of_measurement || "pcs",
          available: avail,
          quantity: 1,
        },
      ])
    }
  }

  const changeCartQty = (inventoryId, delta) => {
    setCartItems((prev) => {
      const item = prev.find((c) => c.inventory_id === inventoryId)
      if (!item) return prev
      const next = item.quantity + delta
      if (next <= 0) return prev.filter((c) => c.inventory_id !== inventoryId)
      if (next > item.available) {
        toast.error(`Only ${item.available} available.`)
        return prev
      }
      return prev.map((c) => (c.inventory_id === inventoryId ? { ...c, quantity: next } : c))
    })
  }

  const commitCartQty = (inventoryId) => {
    if (!editingQty || editingQty.id !== inventoryId) return
    const num = parseInt(editingQty.value, 10)
    setEditingQty(null)
    if (isNaN(num) || num <= 0) return
    setCartItems((prev) =>
      prev.map((c) => {
        if (c.inventory_id !== inventoryId) return c
        const clamped = Math.min(num, c.available)
        if (num > c.available) toast.error(`Only ${c.available} available.`)
        return { ...c, quantity: clamped }
      })
    )
  }

  const removeCartItem = (inventoryId) => {
    setCartItems((prev) => prev.filter((c) => c.inventory_id !== inventoryId))
  }

  // --- Computed ---
  const totals = useMemo(() => {
    const totalItems = cartItems.length
    const totalQty = cartItems.reduce((s, c) => s + (c.quantity || 0), 0)
    return { totalItems, totalQty }
  }, [cartItems])

  const filterOptions = useMemo(() => {
    const cats = [...new Set(allItems.map((i) => i.category).filter(Boolean))].sort()
    const subs = [...new Set(allItems.map((i) => i.sub_category).filter(Boolean))].sort()
    const brands = [...new Set(allItems.map((i) => i.brand).filter(Boolean))].sort()
    return { cats, subs, brands }
  }, [allItems])

  const applyLocalFilters = useCallback((items) => {
    let filtered = items
    if (filterCategory.length > 0) filtered = filtered.filter((i) => filterCategory.includes(i.category))
    if (filterSubCategory.length > 0) filtered = filtered.filter((i) => filterSubCategory.includes(i.sub_category))
    if (filterBrand.length > 0) filtered = filtered.filter((i) => filterBrand.includes(i.brand))
    if (filterAvailability.length > 0) {
      filtered = filtered.filter((i) => {
        const avail = i.quantity_available || 0
        if (avail <= 0) return filterAvailability.includes("out")
        if (avail <= 10) return filterAvailability.includes("low")
        return filterAvailability.includes("available")
      })
    }
    return filtered
  }, [filterCategory, filterSubCategory, filterBrand, filterAvailability])

  const displayItems = useMemo(() => {
    const base = itemSearch.trim() ? (searchedItems || []) : allItems
    let filtered = applyLocalFilters(base)
    if (sortCol) {
      filtered = [...filtered].sort((a, b) => {
        let va = "", vb = ""
        if (sortCol === "item") { va = a.item_name || ""; vb = b.item_name || "" }
        else if (sortCol === "category") { va = a.category || ""; vb = b.category || "" }
        else if (sortCol === "sub_category") { va = a.sub_category || ""; vb = b.sub_category || "" }
        const cmp = va.localeCompare(vb, "en", { sensitivity: "base" })
        return sortDir === "asc" ? cmp : -cmp
      })
    }
    return filtered.slice(0, 30)
  }, [itemSearch, allItems, searchedItems, applyLocalFilters, sortCol, sortDir])

  // --- Search ---
  const handleSearchKeyDown = async (e) => {
    if (e.key !== "Enter") return
    const q = itemSearch.trim()
    if (!q) return
    e.preventDefault()
    try {
      const items = !searching && searchedItems ? searchedItems : await searchInventory(q)
      const match = applyLocalFilters(items).find((i) => (i.quantity_available || 0) > 0)
      if (match) {
        addToCartByOne(match)
        e.target.select()
      } else {
        toast.error("No matching item in stock.")
      }
    } catch {
      toast.error("Search failed.")
    }
  }

  // --- Customer ---
  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.slice(0, 5)
    const q = customerSearch.toLowerCase()
    return customers.filter((c) => c.name?.toLowerCase().includes(q) || c.phone?.includes(q)).slice(0, 5)
  }, [customers, customerSearch])

  const handleCreateCustomer = async () => {
    if (!customerSearch.trim()) return
    setCreatingCustomer(true)
    try {
      const res = await createCustomer({ name: customerSearch.trim() })
      const newCust = res.data
      setCustomers((prev) => [newCust, ...prev])
      setSelectedCustomer(newCust)
      setCustomerSearch(newCust.name)
      setShowCustomerDrop(false)
      setOldDues(0)
      toast.success("Customer created")
    } catch {
      toast.error("Failed to create customer")
    } finally {
      setCreatingCustomer(false)
    }
  }

  const fetchOldDues = async (customerId) => {
    setLoadingDues(true)
    try {
      const res = await fetchCustomerLedger(customerId, { shouldPaginate: false, limit: 1 })
      setOldDues(res.summary?.total_due || 0)
    } catch {
      setOldDues(0)
    } finally {
      setLoadingDues(false)
    }
  }

  const selectCustomer = (c) => {
    setSelectedCustomer(c)
    setCustomerSearch(c.name)
    setShowCustomerDrop(false)
    fetchOldDues(c._id)
  }

  // --- Payment / Due ---
  useEffect(() => {
    const total = parseFloat(issueTotal) || 0
    const paid = parseFloat(issuePaid) || 0
    const newDue =
      paymentMode === "yes" ? 0 : paymentMode === "partial" ? Math.max(total - paid, 0) : paymentMode === "no" ? total : 0
    setIssueDue(paymentMode ? (oldDues + newDue).toString() : "")
  }, [issueTotal, issuePaid, paymentMode, oldDues])

  const dueNoteContent = useMemo(() => {
    const bill = parseFloat(issueTotal) || 0
    const received = parseFloat(issuePaid) || 0
    const custName = selectedCustomer?.name || customerSearch.trim() || "this customer"
    if (paymentMode === "yes") {
      if (bill > 0) return { text: `Received ${money(bill)} · No dues`, type: "success" }
      return { text: "No due — fully settled.", type: "success" }
    }
    if (paymentMode === "partial") {
      if (bill <= 0) return { text: "Enter the bill amount to calculate what goes to khata.", type: "neutral" }
      const due = Math.max(bill - received, 0)
      return due > 0
        ? { text: `Received ${money(received)} · Due ${money(due)} will be added to ${custName}'s khata.`, type: "warn" }
        : { text: `Received ${money(received)} · No dues`, type: "success" }
    }
    if (paymentMode === "no") {
      if (bill <= 0) return { text: "Enter the bill amount to log this sale to khata.", type: "neutral" }
      return { text: `Received ${money(0)} · Due ${money(bill)} will be added to ${custName}'s khata.`, type: "warn" }
    }
    return { text: "", type: "neutral" }
  }, [paymentMode, issueTotal, issuePaid, selectedCustomer, customerSearch])

  // --- Submit ---
  const hasQtyErrors = cartItems.some((c) => c.quantity > c.available || c.quantity <= 0)

  const handleSubmit = async () => {
    if (submittingRef.current) return
    if (cartItems.length === 0) return toast.error("Add at least one item")
    if (hasQtyErrors) return toast.error("Fix quantity errors before submitting")

    if (paymentMode && paymentMode !== "yes") {
      const total = parseFloat(issueTotal) || 0
      if (total === 0) return toast.error("Enter total bill amount")
      if (!selectedCustomer) return toast.error("Party name is required for partial/unpaid payments")
    }

    submittingRef.current = true
    setSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      const total = parseFloat(issueTotal) || 0
      let paid = 0
      if (paymentMode === "yes") paid = total
      else if (paymentMode === "partial") paid = parseFloat(issuePaid) || 0

      const body = {
        items: cartItems.map((c) => ({ inventory_id: c.inventory_id, quantity: c.quantity })),
        issued_to: selectedCustomer?.name || "Walk-in customer",
        customer_name: selectedCustomer?.name || "Walk-in customer",
        customer_id: selectedCustomer?._id || undefined,
        issue_date: new Date().toISOString(),
        notes: issueNotes,
        record_ledger: !!(paymentMode && selectedCustomer),
        total_amount: total,
        paid_amount: paid,
        due_date: issueDueDate || undefined,
      }

      const res = await fetch(API_ENDPOINTS.inventoryBulkIssue, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || "Failed to issue")

      const { successful, failed } = data.results
      if (successful.length > 0) toast.success(`${successful.length} item(s) issued successfully`)
      if (failed.length > 0) {
        const demandItems = failed.filter((f) => f.demand_created)
        const otherFailed = failed.filter((f) => !f.demand_created)
        if (demandItems.length > 0)
          toast(`${demandItems.length} item(s) added to demand list (insufficient stock)`, { icon: "📋", duration: 5000 })
        if (otherFailed.length > 0)
          toast.error(`${otherFailed.length} item(s) failed: ${otherFailed.map((f) => f.reason).join(", ")}`)
      }

      loadIssueTodayStats()

      onSuccess?.()
      resetForm()
      loadAllItems()
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } catch (e) {
      toast.error(e.message || "Failed to process issue")
    } finally {
      setSubmitting(false)
      submittingRef.current = false
    }
  }

  // --- Keyboard shortcuts ---
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault()
        handleSubmit()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open, handleSubmit])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1280px] w-[95vw] max-h-[92vh] overflow-hidden p-0 gap-0 [&>button.absolute]:hidden">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-white flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 tracking-tight">Quick Issue · Counter Checkout</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Build the basket, tag the customer, log payment status — then issue everything in one click.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-500 flex items-center gap-1.5">
              QTY issued today <b className="font-mono font-semibold text-gray-900">{statQtyToday}</b>
            </span>
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-500 flex items-center gap-1.5">
              SKUs issued today <b className="font-mono font-semibold text-gray-900">{statSkusToday}</b>
            </span>
            <button onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-gray-600 p-1 ml-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="flex overflow-hidden" style={{ height: "calc(92vh - 76px)" }}>
          {/* ── LEFT PANEL ── */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F5F6F9]">
            {/* Search */}
            <div className="bg-white border border-gray-200 rounded-[10px] p-4">
              <div className="text-[13px] font-bold text-gray-900 mb-0.5">Find an item</div>
              <div className="text-xs text-gray-500 mb-3">Scan a barcode, or type and press Enter to add the top match instantly.</div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by item, SKU, category…"
                  autoComplete="off"
                  className="w-full pl-9 pr-4 py-3 border-[1.5px] border-gray-300 rounded-[9px] text-sm outline-none transition-all focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
              <p className="text-[11.5px] text-gray-400 mt-1.5">
                Tip: <kbd className="bg-gray-100 border border-gray-300 rounded px-1.5 py-px font-mono text-[10.5px] text-gray-500">Enter</kbd>{" "}
                adds the first matching result with qty 1, no mouse needed.
              </p>
            </div>

            {/* All items table */}
            <div className="bg-white border border-gray-200 rounded-[10px] p-4">
              <div className="text-[13px] font-bold text-gray-900 mb-0.5">All items</div>
              <div className="text-xs text-gray-500 mb-3">
                {itemSearch.trim()
                  ? searching
                    ? "Searching…"
                    : `${displayItems.length} result(s) for "${itemSearch}"`
                  : `Showing catalog (${allItems.length} items)`}
              </div>

              {/* Filters */}
              <div className="flex gap-2 mb-3 flex-wrap">
                <MultiFilter
                  label="Category"
                  options={filterOptions.cats.map((c) => ({ value: c, label: c }))}
                  selected={filterCategory}
                  onChange={setFilterCategory}
                />
                <MultiFilter
                  label="Sub Category"
                  options={filterOptions.subs.map((s) => ({ value: s, label: s }))}
                  selected={filterSubCategory}
                  onChange={setFilterSubCategory}
                />
                <MultiFilter
                  label="Brand"
                  options={filterOptions.brands.map((b) => ({ value: b, label: b }))}
                  selected={filterBrand}
                  onChange={setFilterBrand}
                />
                <MultiFilter
                  label="Available"
                  options={[
                    { value: "available", label: "In stock" },
                    { value: "low", label: "Low stock" },
                    { value: "out", label: "Out of stock" },
                  ]}
                  selected={filterAvailability}
                  onChange={setFilterAvailability}
                />
              </div>

              <table className="w-full text-[13px]">
                <thead>
                  <tr>
                    {[
                      { key: "item", label: "Item", sortable: true },
                      { key: "category", label: "Category", sortable: true },
                      { key: "sub_category", label: "Sub Category", sortable: true },
                      { key: "available", label: "Available", sortable: false },
                      { key: "action", label: "", sortable: false },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={col.sortable ? () => {
                          if (sortCol === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc")
                          else { setSortCol(col.key); setSortDir("asc") }
                        } : undefined}
                        className={`text-left text-[11px] uppercase tracking-wider text-gray-400 font-semibold pb-2 border-b border-gray-200 ${
                          col.key === "item" ? "pl-2" : col.key === "action" ? "text-right pr-2" : ""
                        } ${col.sortable ? "cursor-pointer hover:text-gray-600 select-none" : ""}`}
                      >
                        {col.label}
                        {col.sortable && sortCol === col.key && (
                          <span className="ml-1 text-blue-500">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-gray-400 py-5 text-[13px]">
                        No items match.
                      </td>
                    </tr>
                  ) : (
                    displayItems.map((item) => {
                      const avail = item.quantity_available || 0
                      const out = avail <= 0
                      const low = avail <= 10
                      return (
                        <tr key={item.inventory_id} className="hover:bg-[#FAFBFD] border-b border-gray-200">
                          <td className="py-2.5 pl-2">
                            <div className="font-semibold text-gray-900">{item.item_name}</div>
                            <div className="text-[11px] font-mono text-gray-400 mt-px">
                              INV-{item.inventory_id}{item.brand ? ` · ${item.brand}` : ""}
                            </div>
                          </td>
                          <td className="py-2.5 text-gray-500">{item.category || "—"}</td>
                          <td className="py-2.5 text-gray-500">{item.sub_category || "—"}</td>
                          <td className="py-2.5">
                            <span
                              className={`text-[11px] font-semibold px-2 py-0.5 rounded-md inline-block ${
                                out
                                  ? "bg-red-50 text-red-600"
                                  : low
                                  ? "bg-amber-50 text-amber-700"
                                  : "bg-green-50 text-green-700"
                              }`}
                            >
                              {out ? "Out of stock" : low ? `Low stock · ${avail}` : `Available · ${avail}`}
                            </span>
                          </td>
                          <td className="py-2.5 text-right pr-2">
                            <button
                              disabled={out}
                              onClick={() => addToCartByOne(item)}
                              className={`border-[1.5px] font-bold text-[12px] px-3 py-1.5 rounded-[7px] transition-all ${
                                out
                                  ? "border-gray-300 text-gray-400 cursor-not-allowed bg-white"
                                  : "border-blue-500 text-blue-500 bg-white hover:bg-blue-500 hover:text-white"
                              }`}
                            >
                              Add
                            </button>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>

          {/* ── RIGHT PANEL — Cart ── */}
          <div className="w-[380px] border-l border-gray-200 bg-white overflow-hidden flex flex-col flex-shrink-0">
            {/* Basket header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[15px] font-extrabold text-gray-900">Basket</h2>
                <span className={`text-[11.5px] font-bold font-mono px-2.5 py-0.5 rounded-full ${totals.totalQty > 0 ? "bg-orange-50 text-orange-700" : "bg-gray-100 text-gray-400"}`}>
                  {totals.totalQty} item{totals.totalQty !== 1 ? "s" : ""}
                </span>
              </div>
              {cartItems.length > 0 && (
                <button
                  onClick={() => setCartItems([])}
                  className="text-[12px] text-red-500 hover:text-red-600 bg-transparent border-none p-0 cursor-pointer font-medium"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-4">

            {/* Customer */}
            <div className="mb-3.5">
              <label className="text-[11.5px] font-bold text-gray-500 uppercase tracking-wide block mb-1.5">
                Customer{" "}
                {(paymentMode === "partial" || paymentMode === "no") && (
                  <span className="text-red-500 font-extrabold">*</span>
                )}
              </label>
              <div className="relative" ref={customerDropRef}>
                <input
                  type="text"
                  placeholder="Search or type a new customer name…"
                  autoComplete="off"
                  className={`w-full px-3 py-2 border-[1.5px] rounded-lg text-[13.5px] outline-none transition-all ${
                    paymentMode !== "yes" && !selectedCustomer && !customerSearch.trim()
                      ? "border-gray-300"
                      : "border-gray-300"
                  } focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]`}
                  value={selectedCustomer ? selectedCustomer.name : customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value)
                    setSelectedCustomer(null)
                    setOldDues(0)
                    setShowCustomerDrop(true)
                  }}
                  onFocus={() => setShowCustomerDrop(true)}
                  onBlur={() => setTimeout(() => setShowCustomerDrop(false), 150)}
                />
                {selectedCustomer && (
                  <button
                    onClick={() => {
                      setSelectedCustomer(null)
                      setCustomerSearch("")
                      setOldDues(0)
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                  >
                    ✕
                  </button>
                )}
                {showCustomerDrop && !selectedCustomer && (
                  <div className="absolute z-50 left-0 right-0 top-[calc(100%+4px)] bg-white border border-gray-300 rounded-[9px] shadow-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.map((c) => (
                      <div
                        key={c._id}
                        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-50 text-[13px]"
                        onMouseDown={() => selectCustomer(c)}
                      >
                        <span className="font-semibold text-gray-900">{c.name}</span>
                        {c.total_due > 0 && (
                          <span className="font-mono text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                            Due {money(c.total_due)}
                          </span>
                        )}
                      </div>
                    ))}
                    {customerSearch.trim() &&
                      !customers.some((c) => c.name?.toLowerCase() === customerSearch.trim().toLowerCase()) && (
                        <div
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-blue-600 font-semibold text-[13px] border-t border-gray-100"
                          onMouseDown={handleCreateCustomer}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          {creatingCustomer ? "Creating..." : `Add "${customerSearch.trim()}" as new customer`}
                        </div>
                      )}
                  </div>
                )}
              </div>
              {selectedCustomer && (
                <div
                  className={`mt-2 text-[12px] px-2.5 py-1.5 rounded-[7px] flex justify-between items-center ${
                    oldDues > 0 ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-gray-50 border border-gray-200 text-gray-500"
                  }`}
                >
                  <span>{oldDues > 0 ? "Existing khata balance" : "No outstanding dues"}</span>
                  <b className="font-mono">{money(oldDues)}</b>
                </div>
              )}
              {!selectedCustomer && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  <button
                      className="text-[11.5px] bg-blue-50 border border-blue-200 rounded-full px-2.5 py-1 text-blue-600 hover:bg-blue-100 hover:border-blue-300 cursor-pointer font-medium"
                      onMouseDown={() => {
                        setSelectedCustomer({ name: "Walk-in customer", _id: null })
                        setCustomerSearch("Walk-in customer")
                        setShowCustomerDrop(false)
                        setOldDues(0)
                      }}
                    >
                      Walk-in customer
                    </button>
                  {customers.filter((c, i, arr) => arr.findIndex((x) => x.name === c.name) === i).slice(0, 4).map((c) => (
                    <button
                      key={c._id}
                      className="text-[11.5px] bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1 text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 cursor-pointer"
                      onMouseDown={() => selectCustomer(c)}
                    >
                      {c.total_due > 0 ? `${c.name} · due ${money(c.total_due)}` : c.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-dashed border-gray-300 my-3.5" />

            {/* Cart items */}
            <div className="min-h-[60px]">
              {cartItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-[13px]">
                  <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-gray-300" strokeWidth={1.6} />
                  Basket is empty.
                  <br />
                  Add items from the left.
                </div>
              ) : (
                cartItems.map((c) => (
                  <div key={c.inventory_id} className="flex items-center gap-2.5 py-2 border-b border-gray-200 last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900 truncate">{c.item_name}</div>
                      <div className="text-[11px] font-mono text-gray-400">{c.sku}</div>
                    </div>
                    <div className="flex items-center border-[1.5px] border-gray-300 rounded-[7px] overflow-hidden">
                      <button
                        onClick={() => changeCartQty(c.inventory_id, -1)}
                        className="w-6 h-[26px] flex items-center justify-center bg-gray-50 hover:bg-blue-50 hover:text-blue-600 font-bold text-sm border-none cursor-pointer"
                      >
                        −
                      </button>
                      <input
                        value={editingQty?.id === c.inventory_id ? editingQty.value : c.quantity}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "")
                          setEditingQty({ id: c.inventory_id, value: v })
                        }}
                        onFocus={(e) => {
                          setEditingQty({ id: c.inventory_id, value: String(c.quantity) })
                          setTimeout(() => e.target.select(), 0)
                        }}
                        onBlur={() => commitCartQty(c.inventory_id)}
                        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur() }}
                        inputMode="numeric"
                        className="w-8 text-center font-mono text-xs font-semibold border-none outline-none py-1"
                      />
                      <button
                        onClick={() => changeCartQty(c.inventory_id, 1)}
                        className="w-6 h-[26px] flex items-center justify-center bg-gray-50 hover:bg-blue-50 hover:text-blue-600 font-bold text-sm border-none cursor-pointer"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => removeCartItem(c.inventory_id)}
                      className="text-gray-400 hover:text-red-500 p-1 border-none bg-transparent cursor-pointer"
                    >
                      <Trash2 className="h-[15px] w-[15px]" />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Summary */}
            <div className="border-t-[1.5px] border-dashed border-gray-300 pt-3 mt-1.5">
              <div className="flex justify-between text-[13px] text-gray-500 mb-1">
                <span>Items</span>
                <span>{totals.totalQty}</span>
              </div>
            </div>

            {/* Payment */}
            <div className="mt-3.5 pt-3.5 border-t-[1.5px] border-dashed border-gray-300">
              <label className="text-[11.5px] font-bold text-gray-500 uppercase tracking-wide block mb-1.5">Payment</label>
              <div className="flex border-[1.5px] border-gray-300 rounded-lg overflow-hidden mb-3">
                {[
                  { value: "yes", label: "Received in full" },
                  { value: "partial", label: "Partial" },
                  { value: "no", label: "Unpaid (Khata)" },
                ].map((opt, idx) => (
                  <button
                    key={opt.value}
                    onClick={() => setPaymentMode(opt.value)}
                    className={`flex-1 py-2.5 text-[12px] font-bold transition-all cursor-pointer border-none ${
                      idx < 2 ? "border-r border-r-gray-300" : ""
                    } ${
                      paymentMode === opt.value
                        ? "bg-blue-600 text-white"
                        : "bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-700"
                    }`}
                    style={idx < 2 ? { borderRight: "1px solid #D2D7E2" } : {}}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Bill amount */}
              <div className="mb-2.5">
                <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">
                  Bill amount{" "}
                  {paymentMode !== "yes" && <span className="text-red-500 font-extrabold">*</span>}
                </label>
                <input
                  type="number"
                  min="0"
                  className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] font-mono outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                  placeholder={paymentMode === "yes" ? "Optional — for your records" : "Required for khata entry"}
                  value={issueTotal}
                  onChange={(e) => setIssueTotal(e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </div>

              {/* Received now (partial only) */}
              {paymentMode === "partial" && (
                <div className="mb-2.5">
                  <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">Amount received now</label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] font-mono outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                    placeholder="0"
                    value={issuePaid}
                    onChange={(e) => setIssuePaid(e.target.value)}
                    onFocus={(e) => e.target.select()}
                  />
                </div>
              )}

              {/* Due date (partial/unpaid) */}
              {(paymentMode === "partial" || paymentMode === "no") && (
                <div className="mb-2.5">
                  <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">Due date</label>
                  <input
                    type="date"
                    className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                    value={issueDueDate}
                    onChange={(e) => {
                      const today = new Date().toISOString().split("T")[0]
                      if (e.target.value && e.target.value < today) {
                        toast.error("Due date cannot be in the past")
                        setIssueDueDate("")
                        return
                      }
                      setIssueDueDate(e.target.value)
                    }}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
              )}

              {/* Due note */}
              <div
                className={`text-[12px] p-2.5 rounded-lg ${
                  dueNoteContent.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : dueNoteContent.type === "warn"
                    ? "bg-amber-50 text-amber-800 border border-amber-200"
                    : "bg-gray-50 text-gray-500 border border-gray-200"
                }`}
              >
                {dueNoteContent.text}
              </div>
            </div>

            {/* Notes */}
            <div className="mt-3">
              <input
                type="text"
                placeholder="Note for this basket (optional)"
                className="w-full px-2.5 py-2 border border-gray-200 rounded-[7px] text-xs outline-none focus:border-blue-500"
                value={issueNotes}
                onChange={(e) => setIssueNotes(e.target.value)}
              />
            </div>

            {/* Issue button */}
            <button
              onClick={handleSubmit}
              disabled={submitting || cartItems.length === 0 || hasQtyErrors}
              className={`w-full rounded-[9px] py-3 px-3.5 text-[14.5px] font-bold mt-3 flex items-center justify-center gap-2 transition-colors border-none cursor-pointer ${
                submitting || cartItems.length === 0 || hasQtyErrors
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#E2620E] hover:bg-[#C2530B] text-white"
              }`}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Issue all items
            </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BulkIssueDialog
